import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { PageHeader } from '../../layouts/Shells';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { CommentsSection } from '../comments/CommentsSection';
import { useTapPeople } from '../projects/hooks';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import {
  SOCIAL_STATUS_LABELS,
  SOCIAL_STATUS_ORDER,
  SOCIAL_STATUS_TONES,
  postsByStatus,
  socialErrorMessage,
  useCreateSocialPost,
  useDeleteSocialPost,
  useSaveSocialTheme,
  useSocialPosts,
  useSocialThemes,
  useUpdateSocialPost,
} from './queries';
import type { SocialPost, SocialStatus } from './queries';
import { CalendarView } from './CalendarView';
import { SocialFilterBar } from './SocialFilterBar';
import { EMPTY_SOCIAL_FILTERS, filterSocialPosts, forApprovalCount } from './filters';
import { ActivityView } from './ActivityView';
import { LINKEDIN_PLATFORMS, formatOptions, linkedinCalendarPosts } from './linkedinCalendar';
import type { SocialFilters } from './filters';
import { platformStyles, personLabel } from './platforms';
import type { CSSProperties } from 'react';

/**
 * Social Studio — Angel's workspace.
 *
 * Two views rather than the six the Apps Script had, because the sheet says
 * which ones earned their place: a board for moving posts along, and a table
 * for scanning the month. Themes sit alongside, since they drive what gets
 * planned rather than being planned themselves.
 *
 * Deliberately absent: campaigns, chunked photo galleries, a Concept field and
 * a CTA field. All were built in the old app; all were empty across 105 live
 * posts. Rebuilding them would be rebuilding the parts nobody adopted.
 */

type View = 'board' | 'calendar' | 'linkedin' | 'table' | 'themes' | 'approval' | 'activity';

// LinkedIn Calendar sits beside Calendar because it IS the calendar, narrowed
// to Angel's LinkedIn plan (mockup approved by Kyu, 14 Oct).
const VIEWS: View[] = ['board', 'calendar', 'linkedin', 'table', 'themes', 'approval', 'activity'];

const VIEW_LABELS: Record<View, string> = {
  board: 'Board',
  calendar: 'Calendar',
  linkedin: 'LinkedIn Calendar',
  table: 'Table',
  themes: 'Thematic Sched',
  approval: 'For Approval',
  activity: 'Activity',
};

export function SocialStudioPage() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<View>('board');
  const [filters, setFilters] = useState<SocialFilters>(EMPTY_SOCIAL_FILTERS);
  const [openId, setOpenId] = useState<string | null>(() => searchParams.get('post'));
  // A date clicked on the calendar: the new-post form opens already on it.
  const [prefillDate, setPrefillDate] = useState<string | null>(null);
  // The LinkedIn Calendar's own Format filter. Separate from the shared
  // filters on purpose: it exists only on that tab, and a filter that carried
  // over to tabs with no Format dropdown would narrow them invisibly.
  const [format, setFormat] = useState('');
  // A refused calendar drag — say why the pill went back where it was.
  const [moveError, setMoveError] = useState('');
  const posts = useSocialPosts();
  const tapPeople = useTapPeople(true);
  const permissions = useMyPermissions();
  const movePost = useUpdateSocialPost();
  const canEditSocial = canEditModule(permissions.data, 'social_studio');

  const people = (tapPeople.data ?? []).map((person) => ({
    id: `tap-person-${person.id}`,
    user_id: person.id,
    project_role: 'member' as const,
    display_name: person.display_name,
    email: person.email,
    avatar_url: person.avatar_url,
  }));

  const allPosts = posts.data ?? [];
  const awaiting = forApprovalCount(allPosts);
  const shown = filterSocialPosts(allPosts, filters);
  const linkedinAll = linkedinCalendarPosts(allPosts, EMPTY_SOCIAL_FILTERS, '');
  const linkedinShown = linkedinCalendarPosts(allPosts, filters, format);
  const onLinkedin = view === 'linkedin';

  // One move path for both calendars: the SAME post, one column changed. The
  // optimistic cache in useUpdateSocialPost is what makes the pill appear on
  // its new day immediately, and return if the database refuses.
  function moveToDate(postId: string, iso: string) {
    setMoveError('');
    movePost.mutate(
      { postId, changes: { publish_date: iso } },
      { onError: (cause: Error) => setMoveError(socialErrorMessage(cause.message)) },
    );
  }

  // The drawer looks up the full list on purpose: editing a post so it no
  // longer matches the filter should not slam the drawer shut mid-edit.
  const open = allPosts.find((post) => post.id === openId);

  // The approval queue ignores the status filter — a queue that can be
  // filtered to exclude its own contents is a trap.
  const awaitingPosts = shown.filter((post) => post.status === 'for_approval');

  const postTitles = new Map(allPosts.map((post) => [post.id, post.title]));
  const personNames = new Map(
    people.map((person) => [
      person.user_id,
      person.display_name.trim() === '' ? person.email : person.display_name,
    ]),
  );

  return (
    <>
      {/* The description stays: "and the monthly themes behind them" is the
          half of this page people do not expect to find here. */}
      <PageHeader
        title="Social Studio"
        topbar
        description="Social content planning — posts, platforms and the monthly themes behind them."
      />

      <div className="tracker-tab-nav" role="group" aria-label="View">
        {VIEWS.map((key) => (
          <button
            key={key}
            type="button"
            className={`tracker-tab-button${view === key ? ' tracker-tab-button--active' : ''}`}
            aria-pressed={view === key}
            onClick={() => setView(key)}
          >
            {VIEW_LABELS[key]}
            {/* Only For Approval carries a count: it is the one tab that is a
                queue, where not knowing the number is the problem. */}
            {key === 'approval' && awaiting > 0 ? (
              <span className="tracker-tab-badge">{awaiting}</span>
            ) : null}
          </button>
        ))}
      </div>

      {posts.isPending ? (
        <Skeleton lines={5} label="Loading posts" />
      ) : posts.isError ? (
        <InlineError>Posts could not be loaded.</InlineError>
      ) : view === 'themes' ? (
        <ThemesView />
      ) : view === 'activity' ? (
        <ActivityView postTitles={postTitles} personNames={personNames} />
      ) : (
        <>
          {/* Themes are not posts, so the bar is absent there rather than
              sitting inert above a list it cannot filter. On the LinkedIn tab
              it counts and offers options from LinkedIn posts only, and the
              platform is a label, not a choice. */}
          <SocialFilterBar
            filters={filters}
            posts={onLinkedin ? linkedinAll : allPosts}
            people={people}
            matched={onLinkedin ? linkedinShown.length : shown.length}
            onChange={setFilters}
            fixedPlatform={onLinkedin ? 'LinkedIn' : undefined}
            format={
              onLinkedin
                ? { value: format, options: formatOptions(linkedinAll), onChange: setFormat }
                : undefined
            }
          />
          {view === 'approval' ? (
            <TableView posts={awaitingPosts} people={people} onOpen={setOpenId} />
          ) : view === 'calendar' ? (
            <>
              {moveError === '' ? null : <InlineError>{moveError}</InlineError>}
              <CalendarView
                posts={shown}
                onOpenPost={setOpenId}
                onAddOnDate={(iso) => setPrefillDate(iso)}
                canDrag={canEditSocial}
                onMovePost={moveToDate}
              />
            </>
          ) : view === 'linkedin' ? (
            <>
              {moveError === '' ? null : <InlineError>{moveError}</InlineError>}
              <CalendarView
                posts={linkedinShown}
                onOpenPost={setOpenId}
                onAddOnDate={(iso) => setPrefillDate(iso)}
                canDrag={canEditSocial}
                onMovePost={moveToDate}
                hidePlatformDots
                showFormat
                showTime
                ariaLabel="LinkedIn publishing calendar"
              />
            </>
          ) : view === 'board' ? (
            <BoardView posts={shown} onOpen={setOpenId} />
          ) : (
            <TableView posts={shown} people={people} onOpen={setOpenId} />
          )}
        </>
      )}

      {prefillDate === null ? null : (
        <NewPostDialog
          date={prefillDate}
          linkedin={onLinkedin}
          onClose={() => setPrefillDate(null)}
        />
      )}

      {open === undefined ? null : (
        <PostDrawer key={open.id} post={open} people={people} onClose={() => setOpenId(null)} />
      )}
    </>
  );
}

function PlatformPills({ platforms }: { platforms: string[] }) {
  const styles = platformStyles(platforms);
  if (styles.length === 0) {
    return <span className="deal-note-empty">—</span>;
  }
  return (
    <span className="platform-pills">
      {styles.map((platform) => (
        <span
          key={platform.key}
          className="platform-pill"
          style={{ '--platform-color': platform.color } as CSSProperties}
        >
          {/* The colour, as a mark rather than as a background behind text.
              White on the platform's own brand colour measured 2.15:1 for
              TikTok — three of five platforms were under the floor, at the
              smallest type in the app, on every row. */}
          <span className="platform-dot" aria-hidden="true" />
          {platform.label}
        </span>
      ))}
    </span>
  );
}

function PersonCell({
  person,
}: {
  person: { display_name: string; email: string; avatar_url: string | null } | undefined;
}) {
  if (person === undefined) {
    return <span className="deal-note-empty">—</span>;
  }
  const label = personLabel(person);
  return (
    <span className="person-cell">
      <Avatar name={label} imageUrl={person.avatar_url} size="sm" />
      <span>{label}</span>
    </span>
  );
}

function NewPostDialog({
  date,
  linkedin,
  onClose,
}: {
  date: string;
  /** Opened from the LinkedIn Calendar: the new post is a LinkedIn post. */
  linkedin: boolean;
  onClose: () => void;
}) {
  const create = useCreateSocialPost();
  const [title, setTitle] = useState('');

  function add() {
    if (title.trim() === '') {
      return;
    }
    create.mutate(
      linkedin
        ? { title: title.trim(), publishDate: date, platforms: LINKEDIN_PLATFORMS }
        : { title: title.trim(), publishDate: date },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open
      title={linkedin ? `New LinkedIn post on ${date}` : `New post on ${date}`}
      onClose={onClose}
    >
      {linkedin ? (
        <p className="drawer-autosave-note">Platform is set to LinkedIn for you.</p>
      ) : null}
      <Input
        label="Title"
        autoFocus
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            add();
          }
        }}
      />
      <div className="deal-add-row">
        <Button disabled={create.isPending} onClick={add}>
          {create.isPending ? 'Adding…' : 'Add post'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}

function NewPostRow() {
  const create = useCreateSocialPost();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');

  return (
    <div className="social-create-bar">
      {/* Labelled rather than placeholder-only: a bare date input reads as
          mm/dd/yyyy and says nothing about what the date is for. */}
      <Input
        label="New post"
        placeholder="What are you planning?"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        label="Publish date"
        type="date"
        value={date}
        onChange={(event) => setDate(event.target.value)}
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          if (title.trim() === '') {
            return;
          }
          create.mutate(
            { title: title.trim(), publishDate: date === '' ? null : date },
            {
              onSuccess: () => {
                setTitle('');
                setDate('');
              },
            },
          );
        }}
      >
        Add
      </Button>
    </div>
  );
}

function BoardView({ posts, onOpen }: { posts: SocialPost[]; onOpen: (id: string) => void }) {
  // Read-only on Social Studio keeps the board and loses the drag handle:
  // offering a drag the database will refuse is a card that springs back with
  // an error nobody can act on.
  const permissions = useMyPermissions();
  const canEdit = canEditModule(permissions.data, 'social_studio');
  const update = useUpdateSocialPost();
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState<SocialStatus | null>(null);
  const grouped = postsByStatus(posts);

  return (
    <>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <NewPostRow />
      <div className="social-board">
        {SOCIAL_STATUS_ORDER.map((status) => (
          <section
            key={status}
            className={`board-column${dragOver === status ? ' board-column--over' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(status);
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(null);
              const id = event.dataTransfer.getData('text/plain');
              if (id === '') {
                return;
              }
              setError('');
              update.mutate(
                { postId: id, changes: { status } },
                {
                  // Approving is admin-only in the database, so a refusal here
                  // is a real answer rather than a glitch — say which.
                  onError: (cause: Error) => setError(socialErrorMessage(cause.message)),
                },
              );
            }}
          >
            <div className="board-column-header">
              <Badge tone={SOCIAL_STATUS_TONES[status]}>{SOCIAL_STATUS_LABELS[status]}</Badge>
              <span className="board-column-count">{grouped.get(status)?.length ?? 0}</span>
            </div>
            <ul className="board-column-cards">
              {(grouped.get(status) ?? []).map((post) => (
                <li key={post.id} className="board-card">
                  <button
                    type="button"
                    className="board-card-button"
                    draggable={canEdit}
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', post.id);
                      event.dataTransfer.effectAllowed = 'move';
                    }}
                    onClick={() => onOpen(post.id)}
                  >
                    <span className="board-card-title">{post.title}</span>
                    <span className="board-card-meta">
                      {post.publish_date === null ? (
                        <span className="deal-note-empty">No date</span>
                      ) : (
                        <span className="board-card-date">{post.publish_date}</span>
                      )}
                      <PlatformPills platforms={post.platforms} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

function TableView({
  posts,
  people,
  onOpen,
}: {
  posts: SocialPost[];
  people: { user_id: string; display_name: string; email: string; avatar_url: string | null }[];
  onOpen: (id: string) => void;
}) {
  if (posts.length === 0) {
    return <EmptyState title="No posts yet" description="Add the first one to start planning." />;
  }
  return (
    <>
      <NewPostRow />
      {/* The card is what makes the alternating rows readable: their fill is a
          small step down from white and resolves to nothing on the page. Fill
          and no stroke, like All Deals and Deal tasks. See .social-table-card. */}
      <div className="social-table-card">
        <table className="ui-table social-table">
          <thead>
            <tr>
              <th>Post</th>
              <th>Platforms</th>
              <th>Publish</th>
              <th>Owner</th>
              <th>Assignee</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => {
              const owner = people.find((person) => person.user_id === post.owner_id);
              const assignee = people.find((person) => person.user_id === post.assignee_id);
              return (
                <tr key={post.id}>
                  <th scope="row">
                    {/* A title is a title, not a button that looks like a
                      control. It reads as text and behaves as a link. */}
                    <button type="button" className="row-title" onClick={() => onOpen(post.id)}>
                      {post.title}
                    </button>
                  </th>
                  <td>
                    <PlatformPills platforms={post.platforms} />
                  </td>
                  <td>{post.publish_date ?? <span className="deal-note-empty">—</span>}</td>
                  <td>
                    <PersonCell person={owner} />
                  </td>
                  <td>
                    <PersonCell person={assignee} />
                  </td>
                  <td>
                    <Badge tone={SOCIAL_STATUS_TONES[post.status]}>
                      {SOCIAL_STATUS_LABELS[post.status]}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ThemesView() {
  const themes = useSocialThemes();
  const save = useSaveSocialTheme();
  const [month, setMonth] = useState('');
  const [theme, setTheme] = useState('');

  return (
    <Panel title="Monthly themes">
      <p className="drawer-autosave-note">
        The theme each month is planned around. Posts are written against these rather than the
        other way round.
      </p>
      {themes.isPending ? (
        <Skeleton lines={3} label="Loading themes" />
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Theme</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {(themes.data ?? []).map((row) => (
              <tr key={row.id}>
                <th scope="row">{row.month}</th>
                <td>{row.theme}</td>
                <td>{row.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="deal-add-row">
        <Input
          label="Month"
          labelHidden
          placeholder="Month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
        <Input
          label="Theme"
          labelHidden
          placeholder="Theme"
          value={theme}
          onChange={(event) => setTheme(event.target.value)}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (month.trim() === '') {
              return;
            }
            save.mutate(
              { month: month.trim(), theme: theme.trim(), notes: '' },
              {
                onSuccess: () => {
                  setMonth('');
                  setTheme('');
                },
              },
            );
          }}
        >
          Add theme
        </Button>
      </div>
    </Panel>
  );
}

function PostDrawer({
  post,
  people,
  onClose,
}: {
  post: SocialPost;
  people: { user_id: string; display_name: string; email: string; avatar_url: string | null }[];
  onClose: () => void;
}) {
  const update = useUpdateSocialPost();
  const remove = useDeleteSocialPost();
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  function save(changes: Parameters<typeof update.mutate>[0]['changes']) {
    setError('');
    update.mutate(
      { postId: post.id, changes },
      { onError: (cause: Error) => setError(socialErrorMessage(cause.message)) },
    );
  }

  return (
    <Drawer
      open
      wide
      title={post.title}
      onClose={onClose}
      menuItems={[
        {
          // The menu only OPENS the confirmation. It used to be the whole
          // flow — first click armed it, second click (in a menu that had
          // already closed) deleted — so the first click looked like nothing
          // happened and "I could not delete it" was the correct reading.
          // The confirmation lives in the drawer body now, where it is
          // visible the moment the menu closes.
          label: 'Delete post…',
          danger: true,
          onSelect: () => setConfirming(true),
        },
      ]}
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {!confirming ? null : (
        <div className="drawer-delete-confirm" role="alert">
          <p>Delete “{post.title}”? This cannot be undone.</p>
          <div className="deal-add-row">
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                setError('');
                remove.mutate(post.id, {
                  onSuccess: onClose,
                  onError: (cause: Error) => {
                    setConfirming(false);
                    setError(socialErrorMessage(cause.message));
                  },
                });
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
        </div>
      )}

      <div className="deal-drawer-badges">
        <Badge tone={SOCIAL_STATUS_TONES[post.status]}>{SOCIAL_STATUS_LABELS[post.status]}</Badge>
        {post.approved_at === null ? null : <Badge tone="success">Approved</Badge>}
      </div>

      <Input
        label="Title"
        defaultValue={post.title}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next !== '' && next !== post.title) {
            save({ title: next });
          }
        }}
      />

      <Input
        label="Platforms"
        defaultValue={post.platforms.join(', ')}
        placeholder="Instagram, TikTok, Facebook"
        onBlur={(event) =>
          save({
            platforms: event.target.value
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean),
          })
        }
      />

      <Input label="Type" defaultValue={post.type} onBlur={(e) => save({ type: e.target.value })} />
      <Input
        label="Pillar"
        defaultValue={post.pillar}
        onBlur={(e) => save({ pillar: e.target.value })}
      />

      <Input
        label="Publish date"
        type="date"
        defaultValue={post.publish_date ?? ''}
        onBlur={(event) =>
          save({ publish_date: event.target.value === '' ? null : event.target.value })
        }
      />

      <Select
        label="Status"
        value={post.status}
        options={SOCIAL_STATUS_ORDER.map((status) => ({
          value: status,
          label: SOCIAL_STATUS_LABELS[status],
        }))}
        onChange={(event) => save({ status: event.target.value as SocialStatus })}
      />

      <Input
        label="Publish time"
        type="time"
        defaultValue={post.publish_time?.slice(0, 5) ?? ''}
        onBlur={(event) =>
          save({ publish_time: event.target.value === '' ? null : event.target.value })
        }
      />

      <Select
        label="Owner"
        value={post.owner_id ?? ''}
        options={[
          { value: '', label: 'Unassigned' },
          ...people.map((person) => ({
            value: person.user_id,
            label: person.display_name.trim() === '' ? person.email : person.display_name,
          })),
        ]}
        onChange={(event) =>
          save({ owner_id: event.target.value === '' ? null : event.target.value })
        }
      />

      <Select
        label="Assignee"
        value={post.assignee_id ?? ''}
        options={[
          { value: '', label: 'Unassigned' },
          ...people.map((person) => ({
            value: person.user_id,
            label: person.display_name.trim() === '' ? person.email : person.display_name,
          })),
        ]}
        onChange={(event) =>
          save({ assignee_id: event.target.value === '' ? null : event.target.value })
        }
      />

      <Textarea
        label="Caption"
        rows={6}
        defaultValue={post.caption}
        onBlur={(event) => save({ caption: event.target.value })}
      />

      <Textarea
        label="Asset links"
        rows={3}
        defaultValue={post.asset_links}
        onBlur={(event) => save({ asset_links: event.target.value })}
      />

      <Textarea
        label="Notes"
        rows={3}
        defaultValue={post.notes ?? ''}
        onBlur={(event) => save({ notes: event.target.value === '' ? null : event.target.value })}
      />

      {/* Comments carry @mentions, attachments and Cmd+V paste already. */}
      <CommentsSection
        taskId={null}
        projectId=""
        socialPostId={post.id}
        canComment
        canManage
        members={people.map((person) => ({
          id: `p-${person.user_id}`,
          user_id: person.user_id,
          project_role: 'member' as const,
          display_name: person.display_name,
          email: person.email,
          avatar_url: person.avatar_url,
        }))}
        heading="Notes & discussion"
      />
    </Drawer>
  );
}
